import {
	Crosshair,
	Maximize2,
	Minimize2,
	Redo2,
	Search,
	Undo2,
	ZoomIn,
	ZoomOut,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import type { BpmnExternalToolbarApi } from './bpmnExternalToolbar'

type BpmnDiagramControlsProps = Pick<
	BpmnExternalToolbarApi,
	| 'loading'
	| 'canUndo'
	| 'canRedo'
	| 'isFullscreen'
	| 'onUndo'
	| 'onRedo'
	| 'onOpenFind'
	| 'onZoomIn'
	| 'onZoomOut'
	| 'onFitViewport'
	| 'onToggleFullscreen'
> & {
	/** When true, attach product-tour data attributes (hoisted global toolbar). */
	showTourTargets?: boolean
}

export function BpmnDiagramControls({
	loading,
	canUndo,
	canRedo,
	isFullscreen,
	onUndo,
	onRedo,
	onOpenFind,
	onZoomIn,
	onZoomOut,
	onFitViewport,
	onToggleFullscreen,
	showTourTargets = false,
}: BpmnDiagramControlsProps) {
	const { t } = useTranslation()

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			<Button
				variant="outline"
				size="sm"
				onClick={onUndo}
				disabled={loading || !canUndo}
				aria-label={t('components.bpmnEditor.undo')}
				title={t('components.bpmnEditor.undo')}
			>
				<Undo2 className="h-4 w-4" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={onRedo}
				disabled={loading || !canRedo}
				aria-label={t('components.bpmnEditor.redo')}
				title={t('components.bpmnEditor.redo')}
			>
				<Redo2 className="h-4 w-4" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={onOpenFind}
				disabled={loading}
				aria-label={t('components.bpmnEditor.findInDiagram')}
				title={t('components.bpmnEditor.findInDiagramTooltip')}
			>
				<Search className="h-4 w-4" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={onZoomIn}
				disabled={loading}
				aria-label={t('components.bpmnEditor.zoomIn')}
				{...(showTourTargets ? { 'data-tour': TOUR.bpmnZoomIn } : {})}
			>
				<ZoomIn className="h-4 w-4" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={onZoomOut}
				disabled={loading}
				aria-label={t('components.bpmnEditor.zoomOut')}
				{...(showTourTargets ? { 'data-tour': TOUR.bpmnZoomOut } : {})}
			>
				<ZoomOut className="h-4 w-4" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={onFitViewport}
				disabled={loading}
				aria-label={t('components.bpmnEditor.fitToViewport')}
			>
				<Crosshair className="h-4 w-4" />
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={onToggleFullscreen}
				disabled={loading}
				aria-label={
					isFullscreen
						? t('components.bpmnEditor.exitFullscreen')
						: t('components.bpmnEditor.enterFullscreen')
				}
			>
				{isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
			</Button>
		</div>
	)
}
